import { useState } from "react";
import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  ChartFrame,
  ChartTooltipCard,
  BarRounded,
  ChartGroup,
  localPoint,
  LegendOrdinal,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { WIDGET_DIMENSIONS, WIDGET_DIMENSION_OPTIONS } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import {
  useShapedSlices,
  sliceDrilldownIds,
  SLICE_SHOW_OPTIONS,
  SLICE_SORT_OPTIONS,
  type DimensionSlice,
} from "#app/widgets/dashboards/use-dimension-slices";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { workloadTitle } from "../shared/derived-titles";

/**
 * `dimension-battery` — a single 100%-stacked horizontal proportion bar (a
 * "battery"): projects grouped by any dimension, each category a rounded segment
 * whose width is its share of the whole. Custom `ChartFrame` path; each segment
 * wears its entity's OWN color via `sliceColors.tokens` (the `LegendOrdinal`
 * reads the same assignment's scale, so segments and legend always line up).
 * Reads the shared `useShapedSlices` plumbing and Show / Sort vocabulary; the
 * tail folds into an "Other" segment so the segments always sum to the whole.
 */
export const dimensionBatteryConfigSchema = z
  .looseObject({
    measure: z.enum(["count"]).default("count"),
    groupBy: z.enum(WIDGET_DIMENSIONS).default("stage"),
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    percentages: z.boolean().default(true),
    legend: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  });

export type DimensionBatteryConfig = z.infer<typeof dimensionBatteryConfigSchema>;

/** A hovered segment, with the svg-relative pointer position for the tooltip. */
type Hover = { x: number; y: number; label: string; total: number };

const ZERO_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

function DimensionBatteryRender({ h, config }: WidgetRenderContext<DimensionBatteryConfig>) {
  const { data, slices, sliceColors, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    sort: config.sort,
    show: config.show,
    filters: config.filters,
  });
  const [hover, setHover] = useState<Hover | null>(null);
  // Click-through: a segment press opens this card's Data drawer on its rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="bar" />;
  }
  if (slices.length === 0) {
    return <WidgetEmpty icon="percent" title="No projects in range" />;
  }

  const total = slices.reduce((sum, d) => sum + d.total, 0);
  const showLegend = config.legend && h >= 3;
  const press =
    drill && instance
      ? (s: DimensionSlice) => {
          setHover(null);
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension: config.groupBy,
            ids: sliceDrilldownIds(s, data, slices),
            label: s.label,
          });
        }
      : undefined;

  const battery = (
    <Box fill="flex" position="relative">
      <ChartFrame ariaLabel="Workload split" margin={ZERO_MARGIN}>
        {({ innerWidth, innerHeight }) => {
          const barHeight = Math.min(innerHeight, 48);
          const y = (innerHeight - barHeight) / 2;
          const gap = slices.length > 1 ? 3 : 0;
          const radius = Math.min(8, barHeight / 2);
          let cursorX = 0;
          return (
            <>
              {slices.map((s, i) => {
                const frac = total > 0 ? s.total / total : 0;
                const x = cursorX;
                const segW = Math.max(0, frac * innerWidth - gap);
                cursorX += frac * innerWidth;
                return (
                  <ChartGroup
                    key={s.id}
                    cursor={press ? "pointer" : undefined}
                    onMouseMove={(e) => {
                      const p = localPoint(e);
                      if (p) setHover({ x: p.x, y: p.y, label: s.label, total: s.total });
                    }}
                    onMouseLeave={() => setHover(null)}
                    onClick={press ? () => press(s) : undefined}
                  >
                    <BarRounded
                      x={x}
                      y={y}
                      width={segW}
                      height={barHeight}
                      radius={radius}
                      all
                      colorToken={sliceColors.tokens[i]}
                    />
                  </ChartGroup>
                );
              })}
            </>
          );
        }}
      </ChartFrame>
      {hover ? (
        <ChartTooltipCard left={hover.x} top={hover.y}>
          <WidgetChartTooltip
            label={hover.label}
            details={`${hover.total} ${hover.total === 1 ? "project" : "projects"}${
              config.percentages
                ? ` · ${total > 0 ? Math.round((hover.total / total) * 100) : 0}%`
                : ""
            }`}
          />
        </ChartTooltipCard>
      ) : null}
    </Box>
  );

  return (
    <Box direction="column" fill="flex" gap="sm" justify="center">
      {battery}
      {showLegend ? (
        <Box direction="row" justify="center">
          <LegendOrdinal
            scale={sliceColors.scale}
            direction="row"
            itemMargin="0 10px 0 0"
            shapeMargin="0 5px 0 0"
          />
        </Box>
      ) : null}
    </Box>
  );
}

const dimensionBatterySettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: WIDGET_DIMENSION_OPTIONS,
      },
      { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: SLICE_SORT_OPTIONS,
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "percentages", kind: "toggle", label: "Show percentages in tooltip", icon: "percent" },
      { key: "legend", kind: "toggle", label: "Show legend", icon: "list" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const dimensionBatteryDef: WidgetModule<DimensionBatteryConfig> = {
  type: "dimension-battery",
  title: "Battery chart",
  dynamicTitle: (config) => workloadTitle(config.title, config.groupBy),
  icon: "percent",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 6, defaultW: 5, defaultH: 3 },
  configSchema: dimensionBatteryConfigSchema,
  settings: dimensionBatterySettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "dimension-battery.custom",
      label: "Battery chart",
      description: "Show the share of projects across a dimension as one stacked bar.",
      section: "Custom",
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
      defaultH: 3,
    },
    {
      key: "dimension-battery.stage",
      label: "Workload by stage",
      description: "The share of projects in each pipeline stage, as one battery bar.",
      section: "Charts",
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage" },
      defaultW: 5,
      defaultH: 3,
      // The label matches the derived title, so stay untitled and let
      // `dynamicTitle` track `groupBy` as the user regroups.
      autoTitle: false,
    },
    {
      key: "dimension-battery.assignee",
      label: "Workload by assignee",
      description: "The share of projects held by each assignee, as one battery bar.",
      section: "Charts",
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee" },
      defaultW: 5,
      defaultH: 3,
      autoTitle: false,
    },
  ],
  render: DimensionBatteryRender,
};
